import { z } from 'zod';

export enum UserRole {
    Admin = 'Admin',
    CustomerAdmin = 'CustomerAdmin',
}

export const UserRoleSchema = z.enum(UserRole);

// Backend auth status response schema
export const AuthStatusSchema = z.object({
    isAuthenticated: z.boolean(),
    userName: z.string().nullable(),
    roles: z.array(UserRoleSchema),
});

export type AuthStatus = z.infer<typeof AuthStatusSchema>;

/**
 * Password requirements:
 * - At least 12 characters
 * - At least 1 lowercase letter
 * - At least 1 uppercase letter
 * - At least 1 number
 * - At least 1 special character
 */
const passwordRegex = {
    minLength: /.{12,}/,
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    number: /[0-9]/,
    specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/,
};

// Form schema includes confirmPassword for validation
export const SetPasswordFormSchema = z
    .object({
        password: z
            .string()
            .min(1, { message: 'Password is required' })
            .refine((val) => passwordRegex.minLength.test(val), {
                message: 'Password must be at least 12 characters',
            })
            .refine((val) => passwordRegex.lowercase.test(val), {
                message: 'Password must contain at least 1 lowercase letter',
            })
            .refine((val) => passwordRegex.uppercase.test(val), {
                message: 'Password must contain at least 1 uppercase letter',
            })
            .refine((val) => passwordRegex.number.test(val), {
                message: 'Password must contain at least 1 number',
            })
            .refine((val) => passwordRegex.specialChar.test(val), {
                message: 'Password must contain at least 1 special character',
            }),
        confirmPassword: z
            .string()
            .min(1, { message: 'Please confirm your password' }),
        token: z.string().optional(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    });

// API request schema matches backend (no confirmPassword)
export const SetPasswordRequestSchema = z.object({
    password: z.string().min(1, { message: 'Password is required' }),
    token: z.string().min(1, { message: 'Token is required' }),
});

export type SetPasswordFormData = z.infer<typeof SetPasswordFormSchema>;
export type SetPasswordRequest = z.infer<typeof SetPasswordRequestSchema>;

export type SetPasswordResponse = {
    success: boolean;
    message?: string;
};
